import { Component, Injector, ChangeDetectionStrategy, ChangeDetectorRef, Input, ViewChild, ElementRef } from '@angular/core';
import { catchError, finalize } from 'rxjs/operators';
import { Observable, of, Subscription } from 'rxjs';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { AppConsts } from '@shared/AppConsts';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { AppComponentBase } from '@shared/common/app-component-base';
import { DynamicDataServiceProxy, RetrieveDynamicDataRequest, TransactionTypeTreeDto } from '@shared/service-proxies/service-proxies';
import { DataSourceItems } from '../dynamic-grid/data-source-items';
import { FileRestrictions, FileState, UploadComponent } from '@progress/kendo-angular-upload';
import { ControlTypeNames, NumericOperators } from '@shared/AppEnums';
import { cloneDeep as _cloneDeep } from 'lodash-es';
import { DropDownTreeComponent } from '@progress/kendo-angular-dropdowns';
import { DropAction, DropPosition, NodeClickEvent, TreeItem, TreeItemDropEvent, TreeItemLookup } from '@progress/kendo-angular-treeview';
import { cancelIcon, plusIcon, insertTopIcon, insertMiddleIcon, insertBottomIcon } from '@progress/kendo-svg-icons';
import { ContextMenuComponent, MenuItem } from '@progress/kendo-angular-menu';

@Component({
    selector: 'dynamic-form',
    templateUrl: './dynamic-form.component.html',
    styleUrls: ['./dynamic-form.component.scss', '../shared/dynamic-components.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [appModuleAnimation()]
})
export class DynamicFormComponent extends AppComponentBase {
    static id = "DynamicFormComponent";
    public defaultItem: { name: string; id: number } = {
        name: this.l("SelectItem"),
        id: null,
    };

    @ViewChild('kendoUploadAttachments') kendoUploadAttachments: ElementRef;
    @ViewChild('dropDownTree') dropDownTree: DropDownTreeComponent;

    @Input() formStructure: any;
    @Input() formId: any;
    @Input() formStructureDataSourceName: string;
    @Input() formDataDataSourceName: string;
    @Input() actionType = AppConsts.actionType.read;
    @Input() retrieveDataBy: string = "";
    @Input() formContents: any;
    @Input() useDefaultActionButton: boolean = true;
    @Input() pageName: string = "";
    @Input() fieldLabelWidth: number = 200;
    @Input() fieldControlWidth: number = 12.4;
    @Input() groupFieldSetWidth: number = 400;
    @Input() groupFieldSetGap: number = 0;
    @Input() isHidden: boolean = false;
    @Input() showBorder: boolean = false;
    @Input() showGuide: boolean = false;
    @Input() useParentComponentData: boolean = false;
    @Input() parentItem: any;
    @Input() isDetailPage = false;
    @Input() formIdentifier: string = "";
    @Input() isPublic = false;
    @Input() formData: any = undefined;
    @Input() otherData: any = undefined;
    @Input() dynamicGridComponents: any;

    readonly controlTypeNames: typeof ControlTypeNames = ControlTypeNames;
    readonly numericOperators: typeof NumericOperators = NumericOperators;

    fieldSets: any[] = [];
    dynamicForm: FormGroup;
    dataSourceItems: DataSourceItems[] = [];
    comboboxFilterValue: string = "";
    uploadSaveUrl = "";
    uploadRemoveUrl = "";
    selectedRow: any = undefined;
    focusedDictionary: string[] = [];
    myRestrictions: FileRestrictions = {
        allowedExtensions: [".jpg", ".png", ".pdf"],
    };
    dynamicFormKendoFormFieldLabelWidth: string = "125px";;
    applyFieldControlWidth: string = "12.4em";
    applyFieldControlWidthForTextBox: string = "12.4em";
    gridGap: string = "0";
    gridTemplateColumns: string = "repeat(auto-fill, minmax(285px, 1fr))";
    border: string = "";
    attachmentsSource: string = "";
    attachmentsName: string = "";
    showAttachmentsGrid: boolean = false;
    numberOfAttachments: number = 3;
    attachmentsHeader: string = "";
    attachmentsIsPdf: boolean;
    dateFieldGridDictionary: { [dateField: string]: any } = {};
    numericFieldGridDictionary: { [numericField: string]: any } = {};
    formChangeSubscription: Subscription;
    inputToolTips: { [id: string]: any } = {};
    comboBoxTitles: { [id: string]: string } = {};
    inputStyles: { [id: string]: string } = {};
    comboBoxData: { [id: string]: any[] } = {};
    originalComboBoxData: { [id: string]: any[] } = {};
    feedbackMade: { [id: string]: boolean } = {};
    focusedInputField: any = undefined;
    arrayControlTypeData: any = {};
    parentArrayFields: any[] = [];
    childArrayFields: any[] = [];
    formBuilt: boolean = false;
    areaData: any[] = [];
    selectedFirstLevelNode: string = '';
    selectedSecondLevelNode: string = '';
    selectedThirdLevelNode: string = '';
    treeViewContextMenuItems: MenuItem[] = [];
    contextItem: any = undefined;
    deletedTreeViewIds: any[] = [];
    treeViewData: any = {
        transactionTypes: [
            //{
            //    id: 1,
            //    typeName: "Test1",
            //    children: [
            //        {
            //            id: 4,
            //            typeName: "Test4",
            //            children: [
            //                {
            //                    id: 5,
            //                    typeName: "Test5"
            //                },
            //                {
            //                    id: 6,
            //                    typeName: "Test6"
            //                },
            //                {
            //                    id: 7,
            //                    typeName: "Test7"
            //                }
            //            ]
            //        }
            //    ]
            //},
            //{
            //    id: 2,
            //    typeName: "Test2"
            //},
            //{
            //    id: 3,
            //    typeName: "Test3",
            //    children: [
            //        {
            //            id: 8,
            //            typeName: "Test8"
            //        }
            //    ]
            //}
        ]
    };

    constructor(
        injector: Injector,
        private _dynamicDataServiceProxy: DynamicDataServiceProxy,
        private cd: ChangeDetectorRef
    ) {
        super(injector);
        this.uploadSaveUrl = AppConsts.remoteServiceBaseUrl + '/Dynamic/UploadAttachments';
    }

    applyFormWidthsAndGap(): void {
        if (this.showBorder) {
            this.border = this.showBorder ? "1px solid #e7d6d6" : "";
        }

        this.dynamicFormKendoFormFieldLabelWidth = this.fieldLabelWidth.toString() + "px";
        this.applyFieldControlWidth = this.fieldControlWidth.toString() + "em";
        this.applyFieldControlWidthForTextBox = (this.fieldControlWidth - 1.4).toString() + "em";
        this.gridTemplateColumns = "repeat(auto-fill, minmax(" + this.groupFieldSetWidth.toString() + "px, 1fr))";
        this.gridGap = this.groupFieldSetGap.toString() + "rem";
        this.cd.markForCheck();
    }

    ngOnChanges() {
        this.applyFormWidthsAndGap();
    }

    ngOnDestroy(): void {
        this.formChangeSubscription?.unsubscribe();
    }

    ngOnInit() {
        if (this.hasDropDownTree()) {
            this.getDropDownTreeData();
        }

        if (this.hasTreeView()) {
            this.getTreeViewData();

            abp.event.on('app.retrieveDataFromGrid', (treeViewData) => {
                this.treeViewData = treeViewData;
            });
        }

        this.treeViewContextMenuItems.push({ text: this.l("EditItem") });
        this.treeViewContextMenuItems.push({ text: this.l("DeleteItem") });

        this.dynamicForm = new FormGroup({});
        if (this.useParentComponentData) {
            this.formContents = this.parentItem;
            this.retrieveFormStructure();
        }
        else {
            if (this.formStructure?.componentSettings?.formName !== undefined) {
                this.retrieveDynamicDataForMainItemsStreamlineaPages(this.formStructure?.componentSettings?.dataSourceName);
            } else {
                this.retrieveDynamicDataForMainItemsDynamicPages(this.formDataDataSourceName);
            }
        }
    }

    handleNodeClick(e: NodeClickEvent, nodeClickEnabled: boolean, treeMenu: ContextMenuComponent) {
        if (e.type === 'contextmenu' && nodeClickEnabled) {
            const originalEvent = e.originalEvent;

            originalEvent.preventDefault();

            this.contextItem = e.item;

            treeMenu.show({
                left: originalEvent.pageX,
                top: originalEvent.pageY,
            });
        }
    }

    onSelect($event, databaseTableColumnName): void {
        if ($event.item.text === this.l("EditItem")) {
            this.editTree = true;
            this.edit(this.contextItem);
        }
        else if ($event.item.text === this.l("DeleteItem")) {
            this.deleteItem(this.contextItem.dataItem, this.treeViewData.transactionTypes);
            this.dynamicForm.value[databaseTableColumnName] = this.treeViewData;
        }
    }


    public activeItem: any;
    public focusEditor: boolean;
    public editTree: boolean;
    public focusIndex: string;
    public textFormControl: FormControl;
    private edit(item): void {
        // skip editing if same node is passed
        if (this.activeItem && this.activeItem.dataItem.typeName === item.dataItem.typeName) {
            return;
        }

        this.activeItem = item;

        this.focusEditor = true; // focus editor on next render
        this.focusIndex = item.index; // book keeping of the edited node index

        this.textFormControl = new FormControl(item.dataItem.typeName, Validators.required);
    }

    public save(databaseTableColumnName): void {
        const currentItem = this.activeItem;

        // return focus to input if invalid
        if (this.textFormControl.invalid) {
            this.focusEditor = true;
            return;
        }

        // update the corresponding dataitem property
        if ((this.textFormControl.value && !this.itemInList(this.textFormControl)) || currentItem.dataItem.typeName === this.textFormControl.value) {
            currentItem.dataItem.typeName = this.textFormControl.value;
        }
        else {
            this.notify.error(this.l("ItemAlreadyInList"));
        }

        if (this.dynamicForm) {
            this.dynamicForm.value[databaseTableColumnName] = this.treeViewData;
            this.dynamicForm.markAsDirty();
        }

        // close the editor
        this.cancel();
    }

    public cancel(): void {
        this.editTree = false;
        this.activeItem = null;
        this.textFormControl = null;
    }

    public handleEditorEnter(event: KeyboardEvent, databaseTableColumnName): void {
        if (event.key === 'Enter') {
            // prevent reopening the editor due to enter press
            event.stopPropagation();

            // persist the changes
            this.save(databaseTableColumnName);
        }
    }

    public handleEditorFocusOut(event: FocusEvent, editorContainer: HTMLElement): void {
        const focusTarget = event.relatedTarget as HTMLElement;

        // close the editor if the new focus target is not part of it
        if (!editorContainer.contains(focusTarget)) {
            this.cancel();
        }
    }

    private deleteItem(dataItem: any, treeViewData = this.treeViewData.transactionTypes) {
        for (let i = 0; i < treeViewData.length; i++) {
            const item = treeViewData[i];

            if (item.typeName === dataItem.typeName) {
                this.deletedTreeViewIds.push(item.id);
                treeViewData.splice(i, 1);
            }

            if (item.children) {
                this.deleteItem(dataItem, item.children);
            }
        }
    }

    hasDropDownTree(): boolean {
        const dropDownTreeKeys = ['NonChartOfAccountsFields', 'HeaderFieldsDefinition', 'DistributionGrid'];
        return dropDownTreeKeys.includes(this.formStructure?.componentSettings?.formLabelKey);
    }

    hasTreeView(): boolean {
        const treeViewKeys = ['TransactionCategoriesAndTypes', 'OrganizationalUnitsAndEntityRelationships', 'ExceptionAndResolutionTypeRelationships', 'TransactionNumberFormat'];
        return treeViewKeys.includes(this.formStructure?.componentSettings?.formLabelKey);
    }

    public getDragStatus(action: DropAction, sourceItem: TreeItemLookup, destinationItem: TreeItemLookup): any {
        if (destinationItem && this.isInvalidDrop(sourceItem, destinationItem, action)) {
            return this.l("Cancel");
        }

        switch (action) {
            case DropAction.Add:
                return this.l("Add");
            case DropAction.InsertTop:
                return this.l("InsertAtTop");
            case DropAction.InsertBottom:
                return this.l("InsertAtBottom");
            case DropAction.InsertMiddle:
                return this.l("InsertInBetween");
            case DropAction.Invalid:
            default:
                return this.l("Cancel");
        }
    }

    public isInvalidDrop(sourceItem, destinationItem, action = null, position = null): boolean {
        const destItemIndex = destinationItem.item.index;
        const destItemIndexCount = (destItemIndex.match(/_/g) || []).length;

        if (sourceItem.children.length > 0 && destItemIndexCount > 0) {
            return true;
        } else if (destItemIndexCount > 1 && (action === DropAction.Add || position === DropPosition.Over)) {
            return true;
        }

        return false;
    }

    public itemInList(textbox) {
        const value = textbox.value;

        const searchTree = (nodes) => {
            for (const node of nodes) {
                if (node.typeName === value) {
                    return true;
                }
                if (node.children && searchTree(node.children)) {
                    return true;
                }
            }
            return false;
        };

        return searchTree(this.treeViewData.transactionTypes);
    }

    public addNode(textbox, treeview, databaseTableColumnName) {
        if (textbox.value && !this.itemInList(textbox)) {
            treeview.data._value.push({ id: undefined, typeName: textbox.value, children: [] });
            
            if (this.dynamicForm) {
                this.dynamicForm.value[databaseTableColumnName] = this.treeViewData;
                this.dynamicForm.markAsDirty();
            }
        }
        else {
            this.notify.error(this.l("ItemAlreadyInList"));
        }
    }

    public checkName(textbox) {
        return textbox?.value?.trim() === "" || textbox?.value === undefined || textbox?.value === null;
    }

    public handleDrop(event: TreeItemDropEvent, databaseTableColumnName: string): void {
        // prevent drop if attempting to add to file
        if (this.dynamicForm && !this.dynamicForm.dirty) {
            this.dynamicForm.markAsDirty();
        }

        if (this.isInvalidDrop(event.sourceItem, event.destinationItem, null, event.dropPosition)) {
            event.setValid(false);
        }
    }

    public isExpanded = (dataItem: any, index: string) => {
        return true;
    };

    public fetchChildren(node: any): Observable<any[]> {
        return of(node.children);
    }

    public hasChildren(node: any): boolean {
        return node.children && node.children.length > 0;
    }

    retrieveDynamicDataForMainItemsStreamlineaPages(dataSourceName: string) {
        let data = {};
        if (this.retrieveDataBy !== "") {
            data['id'] = this.retrieveDataBy;
        }

        if (this.formStructure !== undefined) {
            data['id'] = this.formStructure.componentSettings.formId;
        }

        if (this.useParentComponentData || (this.isPublic && window.location.pathname.split("/")[1] === 'dynamic-page')) {
            this.setFormStructure();
            this.buildForm();
        }
        else {
            if (this.isDetailPage && this.formIdentifier !== '00000000-0000-0000-0000-000000000000') {
                data['formIdentifier'] = this.formIdentifier;
            }

            if (this.permission.isGranted('Pages.Administration.Host.Settings')) {
                data['clientId'] = this.clientAndErpService.clientId;
                data['erpId'] = this.clientAndErpService.erpId;
            }
            else {
                data['clientId'] = undefined;
                data['erpId'] = 888; //NOTE: Use erpId in server
            }

            let request = new RetrieveDynamicDataRequest();
            request.dataSourceName = "FormData" + dataSourceName;
            request.data = data;

            this._dynamicDataServiceProxy.retrieveDynamicData(request)
                .pipe(
                    catchError(() => {
                        return of(undefined);
                    }),
                    finalize(() => {

                    })
                ).subscribe(result => {
                    if (result !== undefined && result?.data !== null) {
                        if (dataSourceName === "FormDataDynamicGridDesignMaintenance") {
                            this.selectedRow = result.data.componentSettings?.createdGrid;
                        }

                        if (result.data?.items != undefined && result.data.items.length > 0) {
                            result.data.items.forEach(item => {
                                item = this.formatItemDate(item);
                            });

                            this.formContents = result.data.items[0];
                        }

                        if (result.data?.asyncItems != undefined && result.data.asyncItems.length > 0) {
                            result.data.asyncItems.forEach(item => {
                                item = this.formatItemDateWithoutId(item);
                            });

                            this.formContents = result.data.asyncItems[0];

                            let multiSelectCombo = this.formStructure.items.flatMap(item => {
                                const comboItemFields = item.fields.filter(x => x.controlTypeId.controlTypeName === this.controlTypeNames.ComboBox && x.optionAndValidation.isMultiSelect);
                                comboItemFields.forEach(x => {
                                    this.formContents[x.databaseTableColumnName] = JSON.parse(this.formContents[x.databaseTableColumnName]);
                                });
                            })

                            const formArrayDataControls = [].concat(...this.formStructure.items.flatMap(item => {
                                // Filter the fields in the item that match the condition
                                const itemFields = item.fields.filter(x => x.controlTypeId.controlTypeName === this.controlTypeNames.FormArray);

                                // Process each child field set
                                const childFieldSetFields = item.childFieldSets.flatMap(childFieldSet => {

                                    // Filter the fields in the child field set that match the condition
                                    return childFieldSet.fields.filter(x => x.controlTypeId.controlTypeName === this.controlTypeNames.FormArray);
                                });

                                // Combine the fields from the item and its child field sets
                                return itemFields.concat(childFieldSetFields);
                            }));

                            //NOTE: Set parentArrayFields and childArrayFields
                            // Initialize arrays for parent and child fields
                            this.parentArrayFields = [];
                            this.childArrayFields = [];
                            this.formStructure.items?.forEach(item => {
                                // Process parent fields
                                item.fields?.forEach(field => {
                                    if (field.controlTypeId.formArrayDataControlType?.formArrayDataFields) {
                                        this.parentArrayFields.push(...field.controlTypeId.formArrayDataControlType.formArrayDataFields);
                                    }
                                });

                                // Process childFieldSet fields
                                item.childFieldSets?.forEach(childFieldSet => {
                                    childFieldSet.fields?.forEach(field => {
                                        if (field.controlTypeId.formArrayDataControlType?.formArrayDataFields) {
                                            this.childArrayFields.push(...field.controlTypeId.formArrayDataControlType.formArrayDataFields);
                                        }
                                    });
                                });
                            });

                            //NOTE: Apply formArrayValue
                            formArrayDataControls.forEach(x => {
                                // Process parent fields
                                for (let index = 0; index < this.parentArrayFields.length; index++) {
                                    // Check if the formContents has the data for the current field
                                    if (this.formContents[x.databaseTableColumnName] !== undefined) {
                                        let formArrayValue = this.formContents[x.databaseTableColumnName][index];
                                        // Assign the value to arrayControlTypeData using the field label key
                                        this.arrayControlTypeData[this.parentArrayFields[index].fieldLabelKey] = formArrayValue;
                                    }
                                }

                                // Process child fields
                                for (let index = 0; index < this.childArrayFields.length; index++) {
                                    // Check if the formContents has the data for the current field
                                    if (this.formContents[x.databaseTableColumnName] !== undefined) {
                                        let formArrayValue = this.formContents[x.databaseTableColumnName][index];
                                        // Assign the value to arrayControlTypeData using the field label key
                                        this.arrayControlTypeData[this.childArrayFields[index].fieldLabelKey] = formArrayValue;
                                    }
                                }
                            });
                        }
                    }

                    this.setFormStructure();
                });
        }
    }

    retrieveFormContent(field) {
        if (this.formContents !== undefined && this.formContents[field.databaseTableColumnName] !== undefined) {
            if (this.formContents[field.databaseTableColumnName] === "true") {
                return true;
            }
            else if (this.formContents[field.databaseTableColumnName] === "false") {
                return false;
            }

            return this.formContents[field.databaseTableColumnName];
        }
        else if (field?.optionAndValidation?.setDefault) {
            return field?.optionAndValidation?.setDefault;
        }
        else if (field?.optionAndValidation?.defaultDate) {
            return DateTime.now().toJSDate();
        }
        else {
            return null;
        }
    }

    labelWidth(dynamicFormKendoFormFieldLabelWidth: string, fieldSetfields: any, index: number) {
        const displayFieldBeside = fieldSetfields[--index]?.displayNextFieldBelowOrBeside === false;
        if (displayFieldBeside) {
            return 'auto';
        }
        else {
            return dynamicFormKendoFormFieldLabelWidth;
        }
    }

    buildCrossFieldValidations(field: any) {
        if ((field.optionAndValidation?.numericGrid !== undefined && field.optionAndValidation?.numericGrid.length > 0)
            || (field.optionAndValidation?.dateGrid !== undefined && field.optionAndValidation?.dateGrid.length > 0)) {
            this.numericFieldGridDictionary = {};
            this.dateFieldGridDictionary = {};
            if (field.optionAndValidation.controlTypeName === AppConsts.formControlTypes.formNumeric) {
                if (field.optionAndValidation?.numericGrid !== undefined && field.optionAndValidation?.numericGrid?.length > 0) {
                    this.numericFieldGridDictionary[field.field] = field.optionAndValidation?.numericGrid;
                }

                this.dynamicForm.addControl(field.databaseTableColumnName,
                    new FormControl(this.retrieveFormContent(field), this.applyValidationSettings(field)));
            }
            if (field.optionAndValidation.controlTypeName === AppConsts.formControlTypes.formDate) {
                const pastThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.pastThan &&
                    x.dateColumnName && x.dateColumnName !== "") > -1;
                const futureThanOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.futureThan &&
                    x.dateColumnName && x.dateColumnName !== "") > -1;
                const betweenOperator = field.optionAndValidation?.dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.between &&
                    x.dateColumnName && x.dateColumnName !== "") > -1;

                if (pastThanOperator || futureThanOperator || betweenOperator) {
                    this.dateFieldGridDictionary[field.field] = field.optionAndValidation?.dateGrid;
                    this.dynamicForm.addControl(field.databaseTableColumnName,
                        new FormControl(this.retrieveFormContent(field), this.applyValidationSettings(field)));
                }
                else {
                    let dateGrid = field.optionAndValidation?.dateGrid;

                    let minimumDate = dateGrid.find(x => x.minimumDate !== undefined && x.minimumDate !== null)?.minimumDate;
                    let maximumDate = dateGrid.find(x => x.maximumDate !== undefined && x.maximumDate !== null)?.maximumDate;

                    if (minimumDate && maximumDate) {
                        this.dynamicForm.addControl(field.databaseTableColumnName,
                            new FormControl(this.retrieveFormContent(field), [...this.applyValidationSettings(field),
                            this.dateInRange(field.databaseTableColumnName, minimumDate, maximumDate, true, true)]));
                    }
                    else if (minimumDate) {
                        this.dynamicForm.addControl(field.databaseTableColumnName,
                            new FormControl(this.retrieveFormContent(field), [...this.applyValidationSettings(field),
                            this.dateLessThan(field.databaseTableColumnName, minimumDate, true)]));
                    }
                    else if (maximumDate) {
                        this.dynamicForm.addControl(field.databaseTableColumnName,
                            new FormControl(this.retrieveFormContent(field), [...this.applyValidationSettings(field),
                            this.dateGreaterThan(field.databaseTableColumnName, maximumDate, true)]));
                    }
                }
            }
        }
        else {
            this.dynamicForm.addControl(field.databaseTableColumnName,
                new FormControl(this.retrieveFormContent(field), this.applyValidationSettings(field)));
        }
    }

    buildForm() {
        for (let fieldSet of this.fieldSets) {
            for (let field of fieldSet.fields) {
                if (field.optionAndValidation?.controlTypeName === AppConsts.formControlTypes.formNumeric || field.optionAndValidation?.controlTypeName === AppConsts.formControlTypes.formDate) {
                    this.buildCrossFieldValidations(field);
                }
                else {
                    if (this.dynamicForm.controls[field.databaseTableColumnName] !== undefined) {
                        this.dynamicForm.removeControl(field.databaseTableColumnName);
                    }

                    this.dynamicForm.addControl(field.databaseTableColumnName,
                        new FormControl(this.retrieveFormContent(field), this.applyValidationSettings(field)));

                    if (field.optionAndValidation?.matchPassword) {
                        this.dynamicForm.addControl("confirmPassword", new FormControl("", Validators.required));

                        if (this.dynamicForm.validator !== null) {
                            this.dynamicForm.setValidators([this.passwordMatchValidator(field.databaseTableColumnName), this.dynamicForm.validator]);
                        }
                        else {
                            this.dynamicForm.setValidators(this.passwordMatchValidator(field.databaseTableColumnName));
                        }
                    }
                }
            }

            if (fieldSet.childFieldSets) {
                for (let fieldSet2 of fieldSet.childFieldSets) {
                    for (let field2 of fieldSet2.fields) {
                        if (field2.optionAndValidation?.controlTypeName === AppConsts.formControlTypes.formNumeric || field2.optionAndValidation?.controlTypeName === AppConsts.formControlTypes.formDate) {
                            this.buildCrossFieldValidations(field2);
                        }
                        else {
                            this.dynamicForm.addControl(field2.databaseTableColumnName,
                                new FormControl(this.retrieveFormContent(field2), this.applyValidationSettings(field2)));
                        }
                    }
                }
            }
        }

        for (var key in this.numericFieldGridDictionary) {
            if (this.dynamicForm.validator !== null) {
                this.dynamicForm.setValidators([this.numericGridValidator(key, this.numericFieldGridDictionary[key]), this.dynamicForm.validator]);
            }
            else {
                this.dynamicForm.setValidators(this.numericGridValidator(key, this.numericFieldGridDictionary[key]));
            }
        }

        for (var key in this.dateFieldGridDictionary) {
            let dateGrid = this.dateFieldGridDictionary[key];
            if (dateGrid !== undefined && dateGrid.length > 0) {
                // Checks if Date Grid is using the Past Than Operator
                if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.pastThan && x.dateColumnName && x.dateColumnName !== "") > -1) {
                    let value = dateGrid.find(x => x.operator === AppConsts.dateOperators.pastThan);

                    if (this.dynamicForm.validator !== null) {
                        this.dynamicForm.setValidators([this.dateLessThan(key, value.dateColumnName), this.dynamicForm.validator]);
                    }
                    else {
                        this.dynamicForm.setValidators(this.dateLessThan(key, value.dateColumnName));
                    }
                }
                // Checks if Date Grid is using the Future Than Operator
                else if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.futureThan && x.dateColumnName && x.dateColumnName !== "") > -1) {
                    let value = dateGrid.find(x => x.operator === AppConsts.dateOperators.futureThan);

                    if (this.dynamicForm.validator !== null) {
                        this.dynamicForm.setValidators([this.dateGreaterThan(key, value.dateColumnName), this.dynamicForm.validator]);
                    }
                    else {
                        this.dynamicForm.setValidators(this.dateGreaterThan(key, value.dateColumnName));
                    }
                }
                // Checks if Date Grid is using the Between Operator
                else if (dateGrid.findIndex(x => x.operator === AppConsts.dateOperators.between && x.dateColumnName && x.dateColumnName !== "") > -1) {
                    let firstDate = dateGrid.find(x => x.operator === AppConsts.dateOperators.between).dateColumnName;
                    let secondDate = dateGrid.find(x => x.operator === AppConsts.dateOperators.noDate).dateColumnName;

                    if (this.dynamicForm.validator !== null) {
                        this.dynamicForm.setValidators([this.dateInRange(key, firstDate, secondDate), this.dynamicForm.validator]);
                    }
                    else {
                        this.dynamicForm.setValidators(this.dateInRange(key, firstDate, secondDate));
                    }
                }
            }
        }

        if (this.actionType === AppConsts.actionType.create) {
            this.dynamicForm.reset();
            this.dynamicForm.markAsPristine();
            this.dynamicForm.markAsUntouched();

            //TODO: this should come from the database
            this.dynamicForm.patchValue({ billDate: DateTime.now().toJSDate() });

            // Set the boolean value for checkbox inputs because it's affecting the form if the input is set to null
            for (let key in this.formContents) {
                if (typeof this.formContents[key] === 'boolean') {
                    this.dynamicForm.patchValue({ [key]: false });
                }
            }
        }

        if (this.formData !== undefined) {
            this.dynamicForm.patchValue(this.formData);

            this.dynamicForm.markAsDirty();
        }

        if (this.dynamicForm.valid) {
            abp.event.trigger('app.clearComponentChanges');
        }
        else {
            abp.event.trigger('app.clearComponentChanges', true);
        }

        this.formBuilt = true;

        this.cd.markForCheck();
    }

    applyValidationSettings(field) {
        let validationSettings = [];
        if (field.optionAndValidation !== undefined && field.optionAndValidation !== null) {
            if (field.optionAndValidation.required) {
                validationSettings.push(Validators.required);
            }

            if (field.optionAndValidation.requiredTrue) {
                validationSettings.push(Validators.requiredTrue);
            }

            if (field.optionAndValidation.useEmail) {
                const regex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
                validationSettings.push(Validators.pattern(regex));
            }

            if (field.optionAndValidation.minimumLength > 0) {
                if (field.optionAndValidation.fieldType === "numeric") {
                    validationSettings.push(Validators.min(field.optionAndValidation.minimumLength));
                }
                else {
                    validationSettings.push(Validators.minLength(field.optionAndValidation.minimumLength));
                }
            }

            if (field.optionAndValidation.maximumLength > 0) {
                if (field.optionAndValidation.fieldType === "numeric") {
                    validationSettings.push(Validators.max(field.optionAndValidation.maximumLength));
                }
                else {
                    validationSettings.push(Validators.maxLength(field.optionAndValidation.maximumLength));
                }
            }

            if (field.optionAndValidation?.regexValidation !== undefined && field.optionAndValidation?.regexValidation !== "") {
                validationSettings.push(Validators.pattern(new RegExp(field.optionAndValidation.regexValidation, "i")));
            }
        }
        else if (field.fieldType === 'ARRAY') {
            if (field.controlTypeId.formArrayDataControlType.formArrayDataFields !== null && field.controlTypeId.formArrayDataControlType.formArrayDataFields !== undefined) {
                if (field.controlTypeId.formArrayDataControlType.formArrayDataFields.some(x => x.isRequired)) {
                    validationSettings.push(Validators.required);
                }
            }
        }

        return validationSettings;
    }

    retrieveFormStructure() {
        let data = {};
        if (this.retrieveDataBy !== "") {
            data['id'] = this.retrieveDataBy;
        }

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = this.formStructureDataSourceName;
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                catchError(() => {
                    return of(undefined);
                }),
                finalize(() => {
                    this.cd.markForCheck();
                })
            ).subscribe(result => {
                if (result.data !== null) {
                    result.data.items.forEach(item => {
                        item = this.formatItemDate(item);
                    });

                    this.fieldSets = result.data.items;

                    this.retrieveRequiredData();

                    this.buildForm();

                    this.formChangeSubscription = this.dynamicForm.valueChanges.subscribe(x => {
                        this.updateInputToolTips();
                    });

                    this.updateInputToolTips();

                    this.updateComboBoxTitles();

                    this.disableUneditableFields();

                    this.refreshComboBoxData();

                    this.pdfName = "STREAMLINEA";
                    this.pdfSrc = "data:application/pdf;base64,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";

                    this.runningDocFetch = null;
                    this.isPdfViewerScrollable = true;
                    this.cd.markForCheck();
                }
            });
    }

    setFormStructure() {
        if (this.formStructure !== null && this.formStructure?.items !== undefined) {
            this.formStructure.items.forEach(item => {
                item = this.formatItemDate(item);
            });

            this.fieldSets = this.formStructure.items;

            this.retrieveRequiredData();

            this.formChangeSubscription = this.dynamicForm.valueChanges.subscribe(x => {
                this.updateInputToolTips();
            });

            this.updateInputToolTips();

            this.updateComboBoxTitles();

            this.disableUneditableFields();

            this.refreshComboBoxData();

            if (document.getElementsByClassName("ps--active-y")[0] !== undefined) {
                // This is used to scroll up to the page because this function causes an auto-focus to an input in the grid causing the page to scroll down.
                document.getElementsByClassName("ps--active-y")[0].scrollTo(0, 0);
            }
        }
    }

    private updateInputToolTips() {
        this.fieldSets.forEach(fieldSet => {
            fieldSet?.fields?.forEach(field => {
                this.inputToolTips[field.databaseTableColumnName] = this.applyTooltipError(field, field.databaseTableColumnName);
                this.applyTooltipToFormArrayDataFields(field);
            });

            fieldSet?.childFieldSets?.forEach(childFieldSet => {
                childFieldSet?.fields?.forEach(field => {
                    this.inputToolTips[field.databaseTableColumnName] = this.applyTooltipError(field, field.databaseTableColumnName);
                    this.applyTooltipToFormArrayDataFields(field);
                });
            });
        });
    }

    private applyTooltipToFormArrayDataFields(field: any) {
        if (field.controlTypeId.formArrayDataControlType?.formArrayDataFields) {
            this.parentArrayFields = field.controlTypeId.formArrayDataControlType?.formArrayDataFields;
            this.childArrayFields = field.controlTypeId.formArrayDataControlType?.formArrayDataFields;

            for (var formArrayDataField of field.controlTypeId.formArrayDataControlType?.formArrayDataFields) {
                if (formArrayDataField.isRequired) {
                    this.inputToolTips[formArrayDataField.fieldLabelKey] = this.l("ErrorFieldRequired");
                }
            }
        }
    }

    labelDisplay(hideLabel) {
        if (hideLabel === undefined) {
            return false;
        }

        return hideLabel;
    }

    clearForm() {
        this.dynamicForm.reset();
    }

    deleteForm() {
        this.message.confirm(
            this.l('AreYouSure'),
            this.l('Delete'),
            isConfirmed => {
                if (isConfirmed) {
                    this.dynamicForm.reset();
                    this.notify.success(this.l("ChangesSuccessfullyDeleted"));
                }
            });
    }

    saveForm() {
        this.message.confirm(
            this.l('AreYouSure'),
            this.l('Save'),
            isConfirmed => {
                if (isConfirmed) {
                    this.dynamicForm.reset();
                    this.notify.success(this.l("ChangesSuccessfullySaved"));
                }
            });
    }

    retrieveRequiredData() {
        let dataSourceNames: string[] = [];

        for (let fieldSet of this.fieldSets) {
            for (let field of fieldSet.fields) {
                if (field.dataSourceName) {
                    this.retrieveDynamicMultipleDataItems(field.dataSourceName);
                    dataSourceNames.push(field.dataSourceName);
                }
                else if (this.formStructure?.componentSettings?.formName === "NewClientSignupStep6" && field.fieldLabelKey === "Timezone") {
                    this.retrieveDynamicMultipleDataItems("KeyValuePairTimezones");
                    dataSourceNames.push("KeyValuePairTimezones");
                    field.dataSourceName = "KeyValuePairTimezones";
                }
            }

            if (fieldSet.childFieldSets) {
                for (let fieldSet2 of fieldSet.childFieldSets) {
                    for (let field2 of fieldSet2.fields) {
                        if (field2.dataSourceName) {
                            dataSourceNames.push(field2.dataSourceName);
                            this.retrieveDynamicMultipleDataItems(field2.dataSourceName);
                        }
                    }
                }
            }
        }

        const isDynamicForm = this.formStructure?.componentSettings?.formId !== undefined;
        if (isDynamicForm && this.isPublic === false) {
            this.getDropdownListValueEagerLoaded();
        }
        else if (this.formBuilt === false) {
            this.buildForm();
        }
    }

    retrieveDynamicDataForMainItemsDynamicPages(dataSourceName: string) {
        if (dataSourceName) {
            let data = {};
            if (this.retrieveDataBy !== "") {
                data['id'] = this.retrieveDataBy;
            }

            if (this.retrieveDataBy === null) {
                data['id'] = "1";
            }

            let request = new RetrieveDynamicDataRequest();
            request.dataSourceName = dataSourceName;
            request.data = data;

            this._dynamicDataServiceProxy.retrieveDynamicData(request)
                .pipe(
                    catchError(() => {
                        return of(undefined);
                    }),
                    finalize(() => {

                    })
                ).subscribe(result => {
                    if (result.data !== null) {
                        if (dataSourceName === "FormDataDynamicGridDesignMaintenance") {
                            this.selectedRow = result.data.componentSettings?.createdGrid;
                        }

                        if (result.data.items != undefined && result.data.items.length > 0) {
                            result.data.items.forEach(item => {
                                item = this.formatItemDate(item);
                            });

                            this.formContents = result.data.items[0];

                            this.retrieveFormStructure();
                        }
                    }
                });
        }
        else {
            this.retrieveFormStructure();
        }
    }

    getDropdownListValueEagerLoaded() {
        let data = {};

        data['componentId'] = this.formStructure?.componentSettings?.formId;
        data['componentName'] = this.formStructure?.componentSettings?.formName;
        data['isGridComponent'] = false;

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = "GetDropdownListValueEagerLoaded";
        request.data = data;

        this.buildForm();

        this.disableUneditableFields();

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .subscribe(result => {
                if (result.data !== null) {
                    result.data?.items?.forEach(item => {
                        item = this.formatItemDateWithoutId(item);
                        this.setDataItems(item);
                    });

                    //NOTE: Get All dataSourceItems first before you change the value name to id
                    if ((result.data?.items?.length > 0 && this.formContents) || this.dataSourceItems.length > 0) {
                        this.changeComboBoxValueNameToId();
                    }

                    this.refreshComboBoxData();
                }

                //NOTE: Regardless if there's a dropdown/combobox or not, call buildForm
                this.buildForm();

                this.disableUneditableFields();
            });
    }

    setDataItems(item: any): void {
        let dataSourceItemIndex = this.dataSourceItems.findIndex(x => x.dataSourceName === item.dataSourceName);

        // Updates the value of the data source item when a new item is retrieved.
        if (dataSourceItemIndex > -1 && item.dataSourceItems.length > 0) {
            this.dataSourceItems.splice(dataSourceItemIndex, 1);
        }

        this.dataSourceItems.push(item);
    }

    changeComboBoxValueNameToId(): void {
        let comboBoxFields = [].concat(...this.fieldSets.map(fieldSet => {
            return fieldSet.fields.filter(x => {
                return x.controlTypeId.controlTypeName === this.controlTypeNames.ComboBox ||
                    x.controlTypeId.controlTypeName === this.controlTypeNames.DropDown;
            });
        }));

        this.fieldSets.forEach(fieldSet => fieldSet.childFieldSets.map(x => {
            const comboBoxChildFields = x.fields.filter(x => x.controlTypeId.controlTypeName === this.controlTypeNames.ComboBox || x.controlTypeId.controlTypeName === this.controlTypeNames.DropDown);
            comboBoxFields.push(...comboBoxChildFields);
        }));

        comboBoxFields.map(x => {
            if (this.formContents === undefined) {
                this.formContents = {};
            }

            const idEquivalentOfName = this.dataSourceItems.filter(y => y.dataSourceName === x.dynamicFormFieldId)[0]?.dataSourceItems?.filter(z => z.name === this.formContents[x.databaseTable + "$" + x.databaseField])[0]?.id;

            if (idEquivalentOfName) {
                this.formContents[x.databaseTable + "$" + x.databaseField] = idEquivalentOfName.toString();

                if (this.dynamicForm.controls[x.databaseTable + "$" + x.databaseField] !== undefined) {
                    this.dynamicForm.controls[x.databaseTable + "$" + x.databaseField].patchValue(idEquivalentOfName.toString());
                }
            }
        });
    }

    retrieveDynamicMultipleDataItems(dataSourceName = null) {
        let data = {};
        if (this.retrieveDataBy !== "") {
            data['id'] = this.retrieveDataBy;
        }

        if (this.appSession.tenant === undefined) {
            this.clientAndErpService.isDataSimulation = true;
        }

        if (this.clientAndErpService.isDataSimulation && this.permission.isGranted('Pages.Administration.Host.Settings')) {
            data['clientId'] = this.clientAndErpService.clientId;
            data['erpId'] = this.clientAndErpService.erpId;
        }
        else {
            data['clientId'] = undefined;
            data['erpId'] = 888; //NOTE: Use erpId in server
        }

        let request = new RetrieveDynamicDataRequest();
        request.dataSourceName = dataSourceName;
        request.data = data;

        this._dynamicDataServiceProxy.retrieveDynamicData(request)
            .pipe(
                finalize(() => {

                })
            ).subscribe(result => {
                if (result.data !== null) {
                    result.data.items.forEach(item => {
                        item = this.formatItemDate(item);
                    });

                    this.setItems(dataSourceName, result.data.items);

                    if (this.formContents !== undefined) {
                        this.buildForm();
                    }
                }

                this.refreshComboBoxData();
            });
    }

    setItems(dataSourceName: string, items: any): void {
        let dataSourceItemIndex = this.dataSourceItems.findIndex(x => x.dataSourceName === dataSourceName);

        // Updates the value of the data source item when a new item is retrieved.
        if (dataSourceItemIndex > -1 && items.length > 0) {
            this.dataSourceItems.splice(dataSourceItemIndex, 1);
        }

        let dataSourceItems = new DataSourceItems();
        dataSourceItems.dataSourceName = dataSourceName;
        dataSourceItems.dataSourceItems = items;
        this.dataSourceItems.push(dataSourceItems);
    }

    getItems(dataSourceName: string): any {
        let result: any[];

        result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName).sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;

        return result;
    }

    private disableUneditableFields() {
        this.fieldSets.forEach(fieldSet => {
            fieldSet?.fields.forEach(field => {
                if (field.editable === false) {
                    this.disableUneditableField(field);
                }
            });

            fieldSet?.childFieldSets?.forEach(childFieldSet => {
                childFieldSet?.fields?.filter(field => {
                    this.disableUneditableField(field);
                });
            });
        })
    }

    private disableUneditableField(field: any) {
        if (field.editable === false) {
            this.dynamicForm?.controls[field.databaseTableColumnName]?.disable();
        }
    }

    private refreshComboBoxData() {
        this.fieldSets.forEach(fieldSet => {
            fieldSet?.fields?.filter(field => ((field.controlTypeId.comboBoxType !== null && field.controlTypeId.comboBoxType !== undefined) || field.controlTypeId === ControlTypeNames.ComboBox ||
                (field.controlTypeId.dropdownType !== null && field.controlTypeId.dropdownType !== undefined) || field.controlTypeId === ControlTypeNames.DropDown)
                && ((field.dataSourceName !== undefined && field.dataSourceName !== "") || (field.dynamicFormFieldId !== "" && field.dynamicFormFieldId !== undefined))).forEach(field => {
                    this.comboBoxData[field.databaseTableColumnName] = this.retrieveComboBoxData(field.databaseTableColumnName, field.dynamicFormFieldId, field.dataSourceName,
                        field?.optionAndValidation?.dataDependentTo, field.editable);
                    this.originalComboBoxData[field.databaseTableColumnName] = _cloneDeep(this.comboBoxData[field.databaseTableColumnName]);
                });

            fieldSet?.childFieldSets?.forEach(childFieldSet => {
                childFieldSet?.fields?.filter(field => ((field.controlTypeId.comboBoxType !== null && field.controlTypeId.comboBoxType !== undefined) || field.controlTypeId === ControlTypeNames.ComboBox ||
                    (field.controlTypeId.dropdownType !== null && field.controlTypeId.dropdownType !== undefined) || field.controlTypeId === ControlTypeNames.DropDown)
                    && ((field.dataSourceName !== undefined && field.dataSourceName !== "") || (field.dynamicFormFieldId !== "" && field.dynamicFormFieldId !== undefined))).forEach(field => {
                        this.comboBoxData[field.databaseTableColumnName] = this.retrieveComboBoxData(field.databaseTableColumnName, field.dynamicFormFieldId, field.dataSourceName,
                            field?.optionAndValidation?.dataDependentTo, field.editable);
                        this.originalComboBoxData[field.databaseTableColumnName] = _cloneDeep(this.comboBoxData[field.databaseTableColumnName]);
                    });
            });
        });
    }

    private updateComboBoxTitles() {
        this.fieldSets.forEach(fieldSet => {
            fieldSet?.fields?.filter(field => field.controlTypeId.comboBoxType !== null || field.controlTypeId.dropDownType !== null).forEach(field => {
                var parentField = fieldSet.fields.find(x => x.databaseTableColumnName === field?.optionAndValidation?.dataDependentTo);
                this.comboBoxTitles[field.databaseTableColumnName] = this.getComboBoxTitle(parentField);
            });

            fieldSet?.childFieldSets?.forEach(childFieldSet => {
                childFieldSet?.fields?.filter(field => field.controlTypeId.comboBoxType !== null || field.controlTypeId.dropDownType !== null).forEach(field => {
                    var parentField = childFieldSet.fields.find(x => x.databaseTableColumnName === field?.optionAndValidation?.dataDependentTo);
                    this.comboBoxTitles[field.databaseTableColumnName] = this.getComboBoxTitle(parentField);
                });
            });
        });
    }

    getComboBoxTitle(parentDatabaseField: any) {
        let selectedParentValue = null;
        let comboBoxTitle = "";

        if (parentDatabaseField !== undefined) {
            selectedParentValue = this.dynamicForm.value[parentDatabaseField?.databaseTableColumnName];

            const hasParentButNoSelectedValueYet = parentDatabaseField?.databaseTableColumnName !== "" && (selectedParentValue === "" || selectedParentValue === undefined || selectedParentValue === null);

            if (hasParentButNoSelectedValueYet) {
                comboBoxTitle = this.l("PleaseSelectParentToEnableThisField", parentDatabaseField?.fieldLabelKey);
            }
        }

        return comboBoxTitle;
    }

    retrieveComboBoxData(databaseFieldName: string, dynamicFormFieldId: string, dataSourceName: string, parentDatabaseFieldName: any = null, editable = true): any[] {
        let selectedParentValue = null;

        const dataDependentToHasValue = parentDatabaseFieldName !== "" && parentDatabaseFieldName !== undefined && parentDatabaseFieldName !== null;
        if (dataDependentToHasValue) {
            selectedParentValue = this.dynamicForm.value[parentDatabaseFieldName];
        }

        const thereIsNoSelectedParentValue = (selectedParentValue === "" || selectedParentValue === undefined);
        if (thereIsNoSelectedParentValue) {
            // clear child combobox
            this.dynamicForm.patchValue({ [databaseFieldName]: "" });
        }

        let result = undefined;
        if (dataSourceName === 'ParentDataSource') {
            let parentDataSourceItems = [];
            for (let item of this.parentItem.items.filter(x => x.id !== this.parentItem.dataItem.id && x.id !== "")) {
                parentDataSourceItems.push({ id: item.id, name: item.menuName });
            }
            result = parentDataSourceItems;
        }
        else {
            const isDynamicPages = this.pageName === "";
            const isDynamicFormFieldIdInvalid = dynamicFormFieldId === undefined || dynamicFormFieldId === null;
            if (this.formStructure?.componentSettings?.formName === "NewClientSignupStep6" && dataSourceName === "KeyValuePairTimezones") {
                // do nothing
                // This is for the handling of timezones combobox
            }
            else if (isDynamicPages && isDynamicFormFieldIdInvalid === false) {
                dataSourceName = dynamicFormFieldId;
            }

            result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName).sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;
        }

        if (selectedParentValue !== undefined && selectedParentValue !== null && selectedParentValue !== "") {
            result = result.filter(x => x.parentId === selectedParentValue);

            const selectedValueIsNoLongerAChoice = result.filter(x => x.id === this.dynamicForm.value[databaseFieldName]).length === 0;
            if (selectedValueIsNoLongerAChoice) {
                // clear child combobox
                this.dynamicForm.patchValue({ [databaseFieldName]: "" });
            }
        }

        if (this.comboboxFilterValue !== "") {
            result = result.filter(
                (s) => s.name.toLowerCase().indexOf(this.comboboxFilterValue.toLowerCase()) !== -1
            );
        }

        if ((result === undefined || result.length === 0) || ((selectedParentValue === undefined || selectedParentValue === null) && dataDependentToHasValue) || editable === false) {
            this.dynamicForm?.controls[databaseFieldName]?.disable();
        }
        else {
            this.dynamicForm?.controls[databaseFieldName]?.enable();
        }

        return result;
    }

    onFilterChange(value): void {
        this.comboBoxData[this.focusedInputField] = this.originalComboBoxData[this.focusedInputField].filter(
            (s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1
        );
    }

    showFeedbackMessage(field) {
        if (field !== null && field !== undefined && field.optionAndValidation !== null && field.optionAndValidation !== undefined) {
            if (field.optionAndValidation.feedbackMessage !== '' && field.optionAndValidation.feedbackMessage !== undefined && !this.feedbackMade[field.databaseTableColumnName]) {
                this.message.info(this.l(field.optionAndValidation.feedbackMessage));
                this.feedbackMade[field.databaseTableColumnName] = true;
            }
        }
    }

    onComboBoxValueChange(value, field = null): void {
        this.showFeedbackMessage(field);

        this.updateComboBoxTitles();

        this.refreshComboBoxData();

        this.updateInputStyles();
    }

    onMultiSelectValueChange(value, field = null): void {
        this.showFeedbackMessage(field);

        this.updateComboBoxTitles();

        this.refreshComboBoxData();

        this.updateInputStyles();
    }

    onCheckBoxValueChange(field = null) {
        this.showFeedbackMessage(field);
    }

    onRadioButtonValueChange(field = null) {
        this.showFeedbackMessage(field);
    }

    private updateInputStyles() {
        this.fieldSets.forEach(fieldSet => {
            fieldSet?.fields?.forEach(field => {
                if (field?.controlTypeId?.formArrayDataControlType?.formArrayDataFields !== undefined) {
                    for (var formArrayDataField of field?.controlTypeId?.formArrayDataControlType?.formArrayDataFields) {
                        this.inputStyles[formArrayDataField.fieldLabelKey] = this.isInputRequired(formArrayDataField);
                    }
                }
                else {
                    this.inputStyles[field.databaseTableColumnName] = this.isInputRequired(field);
                }
            });

            fieldSet?.childFieldSets?.forEach(childFieldSet => {
                childFieldSet?.fields?.forEach(field => {
                    if (field?.controlTypeId?.formArrayDataControlType?.formArrayDataFields !== undefined) {
                        for (var formArrayDataField of field?.controlTypeId?.formArrayDataControlType?.formArrayDataFields) {
                            this.inputStyles[formArrayDataField.fieldLabelKey] = this.isInputRequired(formArrayDataField);
                        }
                    }
                    else {
                        this.inputStyles[field.databaseTableColumnName] = this.isInputRequired(field);
                    }
                });
            });
        });
    }

    onTextBoxValueChange(value, field = null): void {
        this.showFeedbackMessage(field);

        this.updateInputStyles();
    }

    onCaptionValueChange(value): void {
        this.dynamicForm.value[value.srcElement.id][0].caption = value.data;

        this.updateInputStyles();
    }

    onArrayTextBoxValueChange(field): void {
        if (field !== null) {
            this.showFeedbackMessage(field);

            var validValue = true;
            for (var formArrayDataField of field.controlTypeId.formArrayDataControlType.formArrayDataFields.filter(x => x.isRequired)) {
                if (this.arrayControlTypeData[formArrayDataField.fieldLabelKey] === undefined || this.arrayControlTypeData[formArrayDataField.fieldLabelKey] === "") {
                    validValue = false;
                }
            }

            if (validValue) {
                this.dynamicForm.patchValue({
                    [field.databaseTableColumnName]: Object.values(this.arrayControlTypeData)
                });
                this.dynamicForm.markAsDirty();
            }
            else {
                this.dynamicForm.patchValue({
                    [field.databaseTableColumnName]: null
                });
                this.dynamicForm.markAsDirty();
            }
        }

        this.cd.markForCheck();

        this.updateInputStyles();
    }

    disableComboBox(field): boolean {
        let dataSourceName = "";
        const isDynamicPages = this.pageName === "";
        const isDynamicFormFieldIdInvalid = field.dynamicFormFieldId === undefined || field.dynamicFormFieldId === null;
        if (isDynamicPages && isDynamicFormFieldIdInvalid === false) {
            dataSourceName = field.dynamicFormFieldId;
        }
        else {
            dataSourceName = field.dataSourceName;
        }

        if (!this.isNullUndefinedOrEmpty(field.optionAndValidation?.dataDependentTo)) {
            const parentValue = this.dynamicForm.get(field.optionAndValidation.dataDependentTo).value;
            if (parentValue === null || parentValue === undefined) {
                return true;
            }
            else {
                let result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName && x.dataSourceItems?.length > 0)
                    .sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;
                result = result.filter(x => x.parentId === parentValue);

                return result === undefined || result.length === 0;
            }
        }
        else {
            let result = this.dataSourceItems.filter(x => x.dataSourceName === dataSourceName && x.dataSourceItems?.length > 0)
                .sort((x, y) => y.dataSourceItems.length - x.dataSourceItems.length)[0]?.dataSourceItems;

            return result === undefined || result.length === 0;
        }
    }

    applyResizableOption(resizeOption) {
        if (resizeOption === "vertical" || resizeOption === "horizontal" || resizeOption === "none") {
            return resizeOption;
        }

        return "both";
    }

    retrieveFileRestrictions(optionAndValidation) {
        let fileRestrictions: FileRestrictions = {};

        if (optionAndValidation.documentType) {
            fileRestrictions.allowedExtensions = [optionAndValidation.documentType];
        }

        if (optionAndValidation.fileSize) {
            fileRestrictions.maxFileSize = optionAndValidation.fileSize;
        }

        return fileRestrictions;
    }

    onTextAreaValueChange(field = null) {
        this.showFeedbackMessage(field);

        this.updateInputStyles();
    }

    onNumericTextBoxValueChange(field = null) {
        this.showFeedbackMessage(field);

        this.updateInputStyles();
    }

    onDatePickerValueChange(field = null) {
        this.showFeedbackMessage(field);
    }

    data: Array<any> = this.createRandomData(1000);
    virtual: any = {
        itemHeight: 28,
        pageSize: 10
    };

    createRandomData(count: number): any[] {
        const firstNames = ['Nancy', 'Andrew', 'Janet', 'Margaret', 'Steven', 'Michael', 'Robert', 'Anne', 'Nige'];
        const lastNames = ['Davolio', 'Fuller', 'Leverling', 'Peacock', 'Buchanan', 'Suyama', 'King', 'Callahan', 'Dodsworth', 'White'];
        const titles = ['Accountant', 'Vice President, Sales', 'Sales Representative', 'Technical Support', 'Sales Manager', 'Web Designer', 'Software Developer'];

        return Array(count).fill({}).map((_, idx) => ({
            id: idx,
            // tslint:disable-next-line:max-line-length
            name: idx + 1 + ' ' + firstNames[Math.floor(Math.random() * firstNames.length)] + ' ' + lastNames[Math.floor(Math.random() * lastNames.length)],
            title: titles[Math.floor(Math.random() * titles.length)]
        }));
    }

    applyTooltipError(field, databaseTableColumnName) {
        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.required) {
            return this.l("ErrorFieldRequired");
        }

        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.pattern) {
            return this.l("ErrorFieldPattern", this.l(field?.fieldLabelKey));
        }

        if (this.dynamicForm.controls[databaseTableColumnName]?.dirty && this.dynamicForm?.errors?.mismatch && field.optionAndValidation.matchPassword) {
            return this.l("ErrorMismatchPassword");
        }

        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.minlength) {
            return this.l("ErrorFieldMinLength", field?.optionAndValidation?.minimumLength);
        }

        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.maxlength) {
            return this.l("ErrorFieldMaxLength", field?.optionAndValidation?.maximumLength);
        }

        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.min) {
            return this.l("ErrorFieldMin", field?.optionAndValidation?.minimumLength);
        }

        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.max) {
            return this.l("ErrorFieldMax", field?.optionAndValidation?.maximumLength);
        }

        if (field?.controlTypeId?.fileUploadType?.fileSize > 0) {
            return this.l("ErrorFieldMax", field?.controlTypeId?.fileUploadType?.fileSize);
        }

        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.numericGrid) {
            if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.numericGrid?.comparatorValue === AppConsts.numericOperators.lessThan) {
                return this.l("ErrorFieldMin", this.dynamicForm.controls[databaseTableColumnName]?.errors?.numericGrid?.totalValue);
            }
            else {
                return this.l("ErrorFieldMax", this.dynamicForm.controls[databaseTableColumnName]?.errors?.numericGrid?.totalValue);
            }
        }

        if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.dateGrid) {
            if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.dateGrid?.minimumDate && this.dynamicForm?.controls[databaseTableColumnName]?.errors?.dateGrid?.maximumDate) {
                return this.l("ErrorFieldRange", this.dynamicForm.controls[databaseTableColumnName]?.errors?.dateGrid?.minimumDate.toLocaleDateString(), this.dynamicForm.controls[databaseTableColumnName]?.errors?.dateGrid?.maximumDate.toLocaleDateString());
            }
            else if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.dateGrid?.minimumDate) {
                return this.l("ErrorFieldMin", this.dynamicForm.controls[databaseTableColumnName]?.errors?.dateGrid?.minimumDate.toLocaleDateString());
            }
            else if (this.dynamicForm?.controls[databaseTableColumnName]?.errors?.dateGrid?.maximumDate) {
                return this.l("ErrorFieldMax", this.dynamicForm.controls[databaseTableColumnName]?.errors?.dateGrid?.maximumDate.toLocaleDateString());
            }
        }
    }

    isRequired(field: any) {
        if (field?.optionAndValidation?.required) {
            return true;
        }

        return false;
    }

    isInputRequired(field: any) {
        let databaseTableColumnName = field.databaseTableColumnName;

        if (field.fieldLabelKey !== undefined && databaseTableColumnName === undefined) {
            var parsedResult = "";

            if (field.isRequired) {
                parsedResult = "border-left-color: red; border-width: 2px; ";

                if (this.arrayControlTypeData[field.fieldLabelKey] === "") {
                    parsedResult += "background-color: rgb(220 53 69 / 25%); box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);";
                }
            }

            return parsedResult;
        }

        if (field.optionAndValidation === undefined || field.optionAndValidation === null)
            return;
        if (this.dynamicForm.controls[databaseTableColumnName]?.dirty === true && this.dynamicForm?.errors?.mismatch === true && field.optionAndValidation?.matchPassword === true) {
            return "background-color: rgb(220 53 69 / 25%);"
        }
        if (field.optionAndValidation.required) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(field.editor) === true) {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (let input of inputList) {
                        if (input.children.length > 0) {
                            input.children[0].style.borderLeftColor = "red";
                            input.children[0].style.borderWidth = "2px";
                            input.children[0].style.backgroundColor = "#FFF";

                            if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && !this.dynamicForm.controls[databaseTableColumnName]?.dirty) {
                                input.children[0].style.backgroundColor = "#FFF";
                            }
                            else if (this.dynamicForm.controls[databaseTableColumnName]?.errors?.customError && this.dynamicForm.controls[databaseTableColumnName]?.dirty) {
                                input.children[0].style.backgroundColor = "yellow";
                            }
                            else if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && this.dynamicForm.controls[databaseTableColumnName]?.dirty) {
                                input.children[0].style.backgroundColor = "rgb(220 53 69 / 25%)";
                            }
                        }
                    }
                }
            }
            else {
                if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && !this.dynamicForm.controls[databaseTableColumnName]?.dirty) {
                    return "border-left-color: red; border-width: 2px;";
                }
                else if (this.dynamicForm.controls[databaseTableColumnName]?.errors?.customError && this.dynamicForm.controls[databaseTableColumnName]?.dirty) {
                    return "border-left-color: red; background-color: yellow; border-width: 2px;";
                }
                else if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && this.dynamicForm.controls[databaseTableColumnName]?.dirty) {
                    return "border-left-color: red; background-color: rgb(220 53 69 / 25%); border-width: 2px;";
                }
                else if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && (this.dynamicForm.controls[databaseTableColumnName]?.dirty || this.dynamicForm.controls[databaseTableColumnName]?.touched) && this.focusedDictionary.indexOf(field) === -1) {
                    return "border-left-color: red; background-color: #FFF; border-width: 2px;";
                }
                else if (databaseTableColumnName.editor === 'boolean') {
                    return "";
                }
                else {
                    return "border-left-color: red; background-color: #FFF; border-width: 2px;";
                }
            }
        }
        this.cd.markForCheck();
    }

    onBlurChange(field: any) {
        let databaseTableColumnName = field.databaseTableColumnName;

        if (field.fieldLabelKey !== undefined && databaseTableColumnName === undefined) {
            if (field.isRequired) {
                this.inputStyles[field.fieldLabelKey] = "border-left-color: red; border-width: 2px; ";

                if (this.arrayControlTypeData[field.fieldLabelKey] === "") {
                    this.inputStyles[field.fieldLabelKey] += "background-color: rgb(220 53 69 / 25%);";
                }
            }
        }

        if (field.optionAndValidation === undefined || field.optionAndValidation === null)
            return;

        if (field.optionAndValidation.required && field.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(field.editor) === true) {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (let input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderRightColor = "";
                        input.children[0].style.borderWidth = "2px";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(databaseTableColumnName);
                input[0].style.borderLeftColor = "red";
                input[0].style.borderWidth = "2px";
                //input[0].style = "border-left-color: red; border-width: 2px;";
            }

            let focusedIndex = this.focusedDictionary.indexOf(databaseTableColumnName);
            if (focusedIndex >= 0) {
                this.focusedDictionary.splice(focusedIndex, 1);
            }
        }
        else if (field.optionAndValidation.required) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(field.editor) === true) {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "#FFF";

                        if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && this.dynamicForm.controls[databaseTableColumnName]?.dirty) {
                            input.style.backgroundColor = "rgb(220 53 69 / 25%)";
                        }
                    }
                }
            }
            else {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.style.borderLeftColor = "red";
                        input.style.borderWidth = "2px";
                        input.style.backgroundColor = "#FFF";

                        var hasInputError = (this.dynamicForm.controls[databaseTableColumnName]?.invalid &&
                            this.dynamicForm.controls[databaseTableColumnName]?.dirty);
                        var hasMatchError = this.dynamicForm.controls[databaseTableColumnName]?.dirty === true &&
                            this.dynamicForm?.errors?.mismatch === true && field.optionAndValidation?.matchPassword === true
                        if (hasInputError || hasMatchError) {
                            input.style.backgroundColor = "rgb(220 53 69 / 25%)";
                        }
                        //input[0].style = "border-left-color: red; border-width: 2px; background-color: #FFF;";
                    }
                }
            }

            let focusedIndex = this.focusedDictionary.indexOf(databaseTableColumnName);
            if (focusedIndex >= 0) {
                this.focusedDictionary.splice(focusedIndex, 1);
            }
        }
        else if (field.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(field.editor) === true) {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (let input of inputList) {
                        input.children[0].style.borderRightColor = "";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "#FFF";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(databaseTableColumnName);
                input[0].style.borderWidth = "2px";
                input[0].style.backgroundColor = "#FFF";
                //input[0].style = "border-width: 2px; background-color: #FFF;";
            }

            let focusedIndex = this.focusedDictionary.indexOf(databaseTableColumnName);
            if (focusedIndex >= 0) {
                this.focusedDictionary.splice(focusedIndex, 1);
            }
        }

        this.refreshComboBoxData();
    }

    onFocusChange(field: any) {
        let databaseTableColumnName = field.databaseTableColumnName;
        this.focusedInputField = databaseTableColumnName;

        if (field.fieldLabelKey !== undefined && databaseTableColumnName === undefined) {
            if (field.isRequired && this.arrayControlTypeData[field.fieldLabelKey] === "") {
                this.inputStyles[field.fieldLabelKey] += "box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);";
            }
        }

        if (field.optionAndValidation === undefined || field.optionAndValidation === null)
            return;

        if (field.optionAndValidation.required && field.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(field.editor) === true) {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (let input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderRightColor = "green";
                        input.children[0].style.borderWidth = "2px";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(databaseTableColumnName);
                input[0].style.borderLeftColor = "red";
                input[0].style.borderRightColor = "green";
                input[0].style.borderWidth = "2px";
                //input[0].style = "border-left-color: red; border-right-color: green; border-width: 2px;";
            }

            if (this.focusedDictionary.indexOf(databaseTableColumnName) === -1) {
                this.focusedDictionary.push(databaseTableColumnName);
            }
        }
        else if (field.optionAndValidation.required) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(field.editor) === true) {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (var input of inputList) {
                        input.children[0].style.borderLeftColor = "red";
                        input.children[0].style.borderWidth = "2px";
                        input.children[0].style.backgroundColor = "rgb(220 53 69 / 25%)";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(databaseTableColumnName);
                input[0].style.borderLeftColor = "red";
                input[0].style.borderWidth = "2px";
                input[0].style.backgroundColor = "rgb(220 53 69 / 25%)";
                //input[0].style = "border-left-color: red; border-width: 2px; background-color: rgb(220 53 69 / 25%);";
                if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && this.dynamicForm.controls[databaseTableColumnName]?.dirty && this.focusedDictionary.indexOf(databaseTableColumnName) > -1) {
                    //input[0].style.borderRightColor = "green";
                    //input[0].style = "border-left-color: red; border-right-color: green; background-color: rgb(220 53 69 / 25%); border-width: 2px;";
                }
                else if (this.dynamicForm.controls[databaseTableColumnName]?.invalid && (this.dynamicForm.controls[databaseTableColumnName]?.dirty || this.dynamicForm.controls[databaseTableColumnName]?.touched) && this.focusedDictionary.indexOf(databaseTableColumnName) === -1) {
                    input[0].style.backgroundColor = "rgb(220 53 69 / 25%)";
                    //input[0].style = "border-left-color: red; border-right-color: green; background-color: #FFF; border-width: 2px;";
                }
                else {
                    input[0].style.backgroundColor = "#FFF";
                    //input[0].style = "border-left-color: red; border-right-color: green; background-color: #FFF; border-width: 2px;";
                }
            }

            if (this.focusedDictionary.indexOf(databaseTableColumnName) === -1) {
                this.focusedDictionary.push(databaseTableColumnName);
            }
        }
        else if (field.optionAndValidation.syncedWithERP) {
            if (this.isKendoInput(field.controlTypeId) === true || this.isKendoEditor(field.editor) === true) {
                let inputList: any = document.getElementsByClassName(databaseTableColumnName);
                if (inputList !== null) {
                    for (let input of inputList) {
                        input.children[0].style.borderRightColor = "green";
                        input.children[0].style.borderWidth = "2px";
                    }
                }
            }
            else {
                let input: any = document.getElementsByClassName(databaseTableColumnName);
                input.children[0].style.borderRightColor = "green";
                input.children[0].style.borderWidth = "2px";
                //input[0].style = "border-right-color: green; border-width: 2px;";
            }

            if (this.focusedDictionary.indexOf(databaseTableColumnName) === -1) {
                this.focusedDictionary.push(databaseTableColumnName);
            }
        }

        this.updateInputStyles();

        this.refreshComboBoxData();
    }

    getDropDownTreeData(): void {
        this._dynamicDataServiceProxy.getDropDownTreeData('00000000-0000-0000-0000-000000000000').subscribe(data => {
            this.areaData = data;
        });
    }

    getTreeViewData(): void {
        // OrganizationalUnitsAndEntityRelationships will take data from the grid component
        if (this.formStructure?.componentSettings?.formLabelKey !== 'OrganizationalUnitsAndEntityRelationships') {
            let clientId = undefined;
            if (this.permission.isGranted('Pages.Administration.Host.Settings')) {
                clientId = this.clientAndErpService.clientId;
            }

            this._dynamicDataServiceProxy.getTreeViewData(clientId).subscribe(data => {
                console.log(data);
                this.treeViewData = data.transactionTypeTree;
            });
        }
    }

    onTreeValueChange(event: any, field: any): void {
        const selectedNodeId = event?.id;

        if (!event) {
            // If event is null or undefined, it means the selection was cleared (e.g., "X" was clicked)
            this.resetSelection();
        } else {
            const selectedNode = this.findNode(this.areaData, selectedNodeId);

            if (selectedNode) {
                const parentNode = this.findParentNode(this.areaData, selectedNode.id);

                if (this.isFirstLevelNode(selectedNode)) {
                    this.notify.warn(this.l('SelectionOfFirstLevelNodesIsNotAllowed'));
                    this.resetSelection();
                    return;
                } else if (parentNode && this.isFirstLevelNode(parentNode)) {
                    this.selectedFirstLevelNode = parentNode.text;
                    this.selectedSecondLevelNode = selectedNode.text;
                    this.selectedThirdLevelNode = '';
                } else if (parentNode) {
                    this.selectedSecondLevelNode = parentNode.text;
                    const firstLevelParent = this.findParentNode(this.areaData, parentNode.id);
                    this.selectedFirstLevelNode = firstLevelParent ? firstLevelParent.text : '';
                    this.selectedThirdLevelNode = selectedNode.text;
                }

                this.triggerGridUpdate(selectedNodeId);
            } else {
                this.resetSelection();
            }
        }
    }

    triggerGridUpdate(id: string): void {
        abp.event.trigger('app.triggerGridUpdate', id);
    }

    resetSelection(): void {
        this.triggerGridUpdate(undefined);
        this.dropDownTree.reset();

        // Reset the selected node properties
        this.selectedFirstLevelNode = '';
        this.selectedSecondLevelNode = '';
        this.selectedThirdLevelNode = '';
    }

    findNode(nodes: any[], id: any): any {
        for (let node of nodes) {
            if (node.id == id) { // Using == for type coercion
                return node;
            }
            if (node.areas && node.areas.length > 0) {
                const found = this.findNode(node.areas, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    findParentNode(nodes: any[], id: any): any {
        for (let node of nodes) {
            if (node.areas && node.areas.some((child: any) => child.id === id)) {
                return node;
            }
            if (node.areas) {
                const found = this.findParentNode(node.areas, id);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    isFirstLevelNode(node: any): boolean {
        return this.areaData.some(item => item.id === node.id);
    }

    openDocumentDialog(): void {
        this.pdfSrc = "";
        this.isPdfViewerVisible = true;
        this.isPdfViewerScrollable = false;

        const billNumber = "147038";
        const billDate = "2021-02-01";
        const invoiceAmount = "83.01";
        const archimedesDocumentId = "";

        this.runningDocFetch = this.disbursementDetailsServiceProxy.getArchimedesDocumentImage(
            billNumber,
            billDate,
            invoiceAmount.toString(),
            archimedesDocumentId)
            .pipe(
                catchError(() => {
                    return of(undefined);
                }),
                finalize(() => {
                    this.runningDocFetch = null;
                    this.isPdfViewerScrollable = true;
                    this.cd.markForCheck();
                })
            )
            .subscribe(result => {
                if (result === undefined) {
                    this.isPdfViewerVisible = false;
                } else if (result !== "") {
                    this.pdfName = "STREAMLINEA";
                    this.pdfSrc = result;
                } else {
                    this.isPdfViewerVisible = false;
                    this.message.info("", this.l('NoAttachedDocument'), {
                        isHtml: false,
                        confirmButtonText: this.l('Ok')
                    });
                }
                this.cd.markForCheck();
            });
    }

    onFieldLabelWidthValueChange(value: any): void {
        this.fieldLabelWidth = value;
        this.applyFormWidthsAndGap();
        this.cd.markForCheck();
    }

    onFieldControlWidthValueChange(value: any): void {
        this.fieldControlWidth = value;
        this.applyFormWidthsAndGap();
        this.cd.markForCheck();
    }

    onGroupFieldSetWidthValueChange(value: any): void {
        this.groupFieldSetWidth = value;
        this.applyFormWidthsAndGap();
        this.cd.markForCheck();
    }

    onGridGapValueChange(value: any): void {
        this.groupFieldSetGap = value;
        this.applyFormWidthsAndGap();
        this.cd.markForCheck();
    }

    onShowBorderValueChange(value: any): void {
        this.showBorder = value.path[0]?.checked;
        this.applyFormWidthsAndGap();
        this.cd.markForCheck();
    }

    successEventHandler($event) {
        const fileUid = $event.files[0].uid;
        const fileResult = $event.response.body?.result;
        const fileSource = fileResult?.fileContentType + fileResult?.fileBytes;

        //this.kendoUploadAttachments["fileList"]._files[fileUid][0].fileSource = fileSource;

        this.cd.markForCheck();
    }

    removeFile(upload: UploadComponent, uid: string): void {
        upload.removeFilesByUid(uid);
    }

    showButton(state: FileState): boolean {
        return state === FileState.Uploaded ? true : false;
    }

    setArrayFieldDataItems(field) {
        if (this.formContents !== undefined) {
            return this.formContents[field.databaseTable + '$' + field.databaseField];
        }
    }

    setArrayFieldAsDirtyIfValueChanged(field, $event): void {
        this.dynamicForm.patchValue({ [field.databaseTableColumnName]: $event });

        this.dynamicForm.controls[field.databaseTableColumnName].markAsDirty();
    }

    getData($event): void {
    }

    cellClick($event): void {
        const item = $event.dataItem;

        if (item.extension === ".pdf") {
            this.attachmentsIsPdf = true;
        } else if (item.extension === ".png" || item.extension === ".jpg") {
            this.attachmentsIsPdf = false;
        }

        this.attachmentsSource = item.fileSource;
        this.attachmentsName = item.fileName + item.extension;
        this.attachmentsHeader = item.attachmentNumber + " - " + item.fileName + item.extension + " - " + item.user + " - " + this.formatDate(item.dateAdded);
    }

    attachmentItems = [{
        id: 0,
        attachmentNumber: "1",
        caption: "caption1",
        fileName: "fileName1",
        user: "user1",
        dateAdded: new Date(),
    }];

    arrayDataItems = [
        {
            id: 0,
            arrayData: "Array Data 1",
        },
        {
            id: 1,
            arrayData: "Array Data 2",
        },
        {
            id: 2,
            arrayData: "Array Data 3",
        }
    ];

    showHideAttachmentsGrid(): void {
        this.showAttachmentsGrid = !this.showAttachmentsGrid;
    }

    getComponentName(): string {
        return "DynamicFormComponent";
    }
}